import { useState, useEffect, useCallback } from 'react';
import { getFollowups, getFollowupCount, createFollowup, getReminders, createReminder } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { ActivityTimeline } from './ActivityTimeline';
import { ReminderModal } from '../modals/ReminderModal';
import { 
  X, Phone, Mail, Globe, MapPin, Star, ExternalLink, 
  Building2, MessageSquare, Users, FileText, Plus, Trash2, Bell, Clock, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addHours } from 'date-fns';

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

export const LeadDetailPanel = ({ lead, onClose, onUpdate, onDelete, onFollowupCreated }) => {
  const [followups, setFollowups] = useState([]);
  const [counts, setCounts] = useState({ total: 0, calls: 0, emails: 0, meetings: 0, notes: 0 });
  const [reminders, setReminders] = useState([]);
  const [showAddFollowup, setShowAddFollowup] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [followupType, setFollowupType] = useState('call');
  const [followupNotes, setFollowupNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  const handleQuickSms = async () => {
    setSendingSms(true);
    try {
      const oneHourFromNow = addHours(new Date(), 1);
      await createReminder(lead.id, oneHourFromNow.toISOString(), `Follow up with ${lead.business_name}`, false, true);
      toast.success('SMS reminder set for 1 hour from now!');
      fetchFollowups();
    } catch (err) {
      toast.error('Failed to set SMS reminder');
    } finally {
      setSendingSms(false);
    }
  };

  const fetchFollowups = useCallback(async () => {
    try {
      const [followupsRes, countsRes, remindersRes] = await Promise.all([
        getFollowups(lead.id),
        getFollowupCount(lead.id),
        getReminders(lead.id)
      ]);
      setFollowups(followupsRes.data);
      setCounts(countsRes.data);
      setReminders(remindersRes.data.filter(r => !r.sent));
    } catch (err) {
      console.error('Failed to fetch followups', err);
    }
  }, [lead.id]);

  useEffect(() => {
    fetchFollowups();
  }, [fetchFollowups]);

  const handleStatusChange = (newStatus) => {
    onUpdate(lead.id, { status: newStatus });
  };

  const handleAddFollowup = async () => {
    if (!followupNotes.trim()) {
      toast.error('Please add notes for the follow-up');
      return;
    }

    setSubmitting(true);
    try {
      await createFollowup(lead.id, followupType, followupNotes);
      toast.success('Follow-up logged');
      setFollowupNotes('');
      setShowAddFollowup(false);
      fetchFollowups();
      onFollowupCreated();
    } catch (err) {
      toast.error('Failed to log follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      onDelete(lead.id);
    }
  };

  return (
    <motion.div 
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="h-full flex flex-col bg-background border-l shadow-xl"
      data-testid="lead-detail-panel"
    >
      {/* Header */}
      <div className="p-4 border-b flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-5 h-5 text-accent" />
            <h2 className="font-heading font-bold text-lg truncate">{lead.business_name}</h2>
          </div>
          {lead.claim_status && (
            <p className="text-xs text-muted-foreground mono-text">{lead.claim_status}</p>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-panel-btn">
          <X className="w-5 h-5" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Status */}
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 block">
              Status
            </label>
            <Select value={lead.status} onValueChange={handleStatusChange}>
              <SelectTrigger data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact Info */}
          <div className="space-y-3">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground block">
              Contact Information
            </label>
            
            {lead.phone && (
              <a href={`tel:${lead.phone}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors group">
                <Phone className="w-4 h-4 text-green-600" />
                <span className="text-sm flex-1">{lead.phone}</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            )}
            
            {lead.email && lead.email !== 'N/A' && (
              <a href={`mailto:${lead.email}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors group">
                <Mail className="w-4 h-4 text-blue-600" />
                <span className="text-sm flex-1 truncate">{lead.email}</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            )}
            
            {lead.website && lead.website !== 'N/A' && (
              <a href={lead.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors group">
                <Globe className="w-4 h-4 text-purple-600" />
                <span className="text-sm flex-1 truncate">{lead.website}</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            )}
            
            {lead.address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(lead.address)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors group">
                <MapPin className="w-4 h-4 text-red-600" />
                <span className="text-sm flex-1">{lead.address}</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            )}

            {lead.gbp_link && (
              <a href={lead.gbp_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors group">
                <Star className="w-4 h-4 text-amber-500" />
                <span className="text-sm flex-1">Google Business Profile</span>
                {lead.rating && (
                  <Badge variant="secondary" className="text-xs">
                    {lead.rating} ★ ({lead.review_count || 0})
                  </Badge>
                )}
              </a>
            )}
          </div>

          {/* SEO Metrics */}
          {(lead.optimization_score || lead.google_ranking || lead.three_pack_ranking || lead.local_ads || lead.paid_ads) && (
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground block">
                SEO Metrics
              </label>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {lead.optimization_score && (
                  <div className="p-2 bg-muted/30 rounded-md">
                    <span className="text-muted-foreground text-xs">Optimization</span>
                    <p className="font-semibold text-accent">{lead.optimization_score}</p>
                  </div>
                )}
                {lead.google_ranking && lead.google_ranking !== 'N/A' && (
                  <div className="p-2 bg-muted/30 rounded-md">
                    <span className="text-muted-foreground text-xs">Google Rank</span>
                    <p className="font-semibold">{lead.google_ranking}</p>
                  </div>
                )}
                {lead.three_pack_ranking && lead.three_pack_ranking !== 'N/A' && (
                  <div className="p-2 bg-muted/30 rounded-md">
                    <span className="text-muted-foreground text-xs">3-Pack</span>
                    <p className="font-semibold text-green-500">{lead.three_pack_ranking}</p>
                  </div>
                )}
                {lead.local_ads && lead.local_ads !== 'N/A' && (
                  <div className="p-2 bg-muted/30 rounded-md">
                    <span className="text-muted-foreground text-xs">Local Ads</span>
                    <p className="font-semibold">{lead.local_ads}</p>
                  </div>
                )}
                {lead.paid_ads && lead.paid_ads !== 'N/A' && (
                  <div className="p-2 bg-muted/30 rounded-md">
                    <span className="text-muted-foreground text-xs">Paid Ads</span>
                    <p className="font-semibold">{lead.paid_ads}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Suggested Services */}
          {lead.suggested_services && (
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2 block">
                AI Suggested Services
              </label>
              <p className="text-sm bg-accent/10 border border-accent/20 p-3 rounded-md">{lead.suggested_services}</p>
            </div>
          )}

          <Separator />

          {/* Follow-up Stats */}
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3 block">
              Follow-up Activity
            </label>
            <div className="grid grid-cols-4 gap-2" data-testid="followup-stats">
              <div className="text-center p-2 bg-muted/30 rounded-md">
                <Phone className="w-4 h-4 mx-auto mb-1 activity-call" />
                <span className="text-lg font-bold block">{counts.calls}</span>
                <span className="text-xs text-muted-foreground">Calls</span>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded-md">
                <Mail className="w-4 h-4 mx-auto mb-1 activity-email" />
                <span className="text-lg font-bold block">{counts.emails}</span>
                <span className="text-xs text-muted-foreground">Emails</span>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded-md">
                <Users className="w-4 h-4 mx-auto mb-1 activity-meeting" />
                <span className="text-lg font-bold block">{counts.meetings}</span>
                <span className="text-xs text-muted-foreground">Meetings</span>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded-md">
                <FileText className="w-4 h-4 mx-auto mb-1 activity-note" />
                <span className="text-lg font-bold block">{counts.notes}</span>
                <span className="text-xs text-muted-foreground">Notes</span>
              </div>
            </div>
          </div>

          {/* Add Follow-up */}
          <div>
            <AnimatePresence>
              {showAddFollowup ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <Select value={followupType} onValueChange={setFollowupType}>
                    <SelectTrigger data-testid="followup-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 activity-call" /> Phone Call
                        </div>
                      </SelectItem>
                      <SelectItem value="email">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 activity-email" /> Email
                        </div>
                      </SelectItem>
                      <SelectItem value="meeting">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 activity-meeting" /> Meeting
                        </div>
                      </SelectItem>
                      <SelectItem value="note">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 activity-note" /> Note
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  <Textarea
                    placeholder="Add notes about this interaction..."
                    value={followupNotes}
                    onChange={(e) => setFollowupNotes(e.target.value)}
                    rows={3}
                    data-testid="followup-notes-input"
                  />

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleAddFollowup} 
                      disabled={submitting}
                      className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
                      data-testid="save-followup-btn"
                    >
                      {submitting ? 'Saving...' : 'Save Follow-up'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAddFollowup(false)}
                      data-testid="cancel-followup-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <div className="space-y-2">
                  {/* Quick SMS Button */}
                  <Button 
                    variant="outline" 
                    className="w-full border-green-500/30 hover:bg-green-500/10 text-green-600"
                    onClick={handleQuickSms}
                    disabled={sendingSms}
                    data-testid="quick-sms-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {sendingSms ? 'Sending...' : 'Text Me in 1 Hour'}
                  </Button>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1" 
                      onClick={() => setShowAddFollowup(true)}
                      data-testid="add-followup-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Log Follow-up
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setShowReminderModal(true)}
                      data-testid="schedule-reminder-btn"
                    >
                      <Bell className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* Scheduled Reminders */}
          {reminders.length > 0 && (
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground block">
                Upcoming Reminders
              </label>
              {reminders.map(reminder => (
                <div key={reminder.id} className="flex items-center gap-2 p-2 bg-accent/10 rounded-md text-sm">
                  <Clock className="w-4 h-4 text-accent" />
                  <span className="flex-1">
                    {format(new Date(reminder.scheduled_at), 'MMM d, h:mm a')}
                  </span>
                  {reminder.message && (
                    <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                      {reminder.message}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <Separator />

          {/* Activity Timeline */}
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3 block">
              Activity Timeline
            </label>
            <ActivityTimeline followups={followups} />
          </div>

          <Separator />

          {/* Delete */}
          <Button 
            variant="outline" 
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleDelete}
            data-testid="delete-lead-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Lead
          </Button>
        </div>
      </ScrollArea>

      {/* Reminder Modal */}
      <ReminderModal
        open={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        lead={lead}
        onCreated={fetchFollowups}
      />
    </motion.div>
  );
};
